import {APIV1Version, ClientType} from '@buncha/common/types/common';
import {Dimensions, StyleSheet} from 'react-native';
import React, {useCallback, useEffect} from 'react';
import {
  useB2BBarcodesContext,
  useUpdateB2BOrderVerifyStatus,
} from '@buncha/B2B/hooks/B2BBarcode';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {Analytics} from '@buncha/config/analytics/analytics';
import {B2BScreens} from '@buncha/B2B/navigation/types';
import {Box} from '@buncha/components/core/view';
import {CustomText} from '@buncha/components/core/text';
import {DeliveryLocationStatus} from '@buncha/batchDetails/types';
import {EventType} from '@buncha/config/analytics/type';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import {OrderVerifyStatus} from '@buncha/B2B/types/B2BBarcode';
import {Skeleton} from 'native-base';
import {navigation} from '@buncha/config/navigation';
import {useGetLocale} from '@buncha/localization/hooks';
import {useSetDeliveryLocationStatusOfAssignedOrders} from '@buncha/batchDetails/hooks/OrderOperation';
import {PButton} from '@buncha/components/core/Button';

interface BarcodeFooterProps {
  runId: number;
  legacyId: number;
  isReScan?: boolean;
}

const BarcodeFooter: React.FC<BarcodeFooterProps> = props => {
  const muliFont = useFontTheme('muli');
  const theme = useColorThemeType();
  const {state} = useB2BBarcodesContext();
  const {body} = useGetLocale(Modules.B2B, B2BScreens.B2BBarcode) ?? {};
  const {updateB2BOrderVerifyStatus, loading} = useUpdateB2BOrderVerifyStatus(
    APIV1Version.V100,
  );
  const [setDeliveryLocationStatusOfAssignedOrders, orderState] =
    useSetDeliveryLocationStatusOfAssignedOrders(null);

  const scannedBarcode = state?.barcodesScannedCount;
  const totalBarcode = state?.listOfBarcodes?.length;
  const isALLBarCodesScanned =
    totalBarcode === scannedBarcode && !props.isReScan;
  const {width} = Dimensions.get('window');
  const BUTTON_DIAMETER = 52;
  const CONTAINER_WIDTH = width - 20;
  const CONTAINER_HEIGHT = BUTTON_DIAMETER + 3;
  const stateLoading = loading || orderState?.loading;

  const handleNavigation = useCallback(() => {
    navigation.goBack();
  }, []);

  const onSubmit = useCallback(async () => {
    if (props?.isReScan) return navigation.goBack();
    await updateB2BOrderVerifyStatus(
      props?.legacyId,
      OrderVerifyStatus.scanSuccessful,
    );
    await setDeliveryLocationStatusOfAssignedOrders(
      props.runId,
      DeliveryLocationStatus.toBeAssigned,
      [props.legacyId],
    );
    Analytics.eventWithProps('POS order scanner', {
      type: EventType.cta,
      runId: props?.runId,
      legacyId: props?.legacyId,
      client: ClientType.meijer,
    });
  }, [
    props.isReScan,
    props.legacyId,
    props.runId,
    setDeliveryLocationStatusOfAssignedOrders,
    updateB2BOrderVerifyStatus,
  ]);

  useEffect(() => {
    if (isALLBarCodesScanned && totalBarcode) handleNavigation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isALLBarCodesScanned, totalBarcode]);

  return (
    <Box style={styles.parentBox}>
      <CustomText
        style={{
          fontFamily: muliFont[900].normal,
          color: theme?.text.blue[100],
        }}>
        {body?.unableToScan}
      </CustomText>
      <If condition={stateLoading}>
        <Skeleton
          height={CONTAINER_HEIGHT}
          width={CONTAINER_WIDTH}
          style={styles.skeleton}
        />
      </If>
      <If condition={!stateLoading}>
        <PButton title={'Tap'} onPress={onSubmit} />
      </If>
    </Box>
  );
};

export default React.memo(BarcodeFooter);
const styles = StyleSheet.create({
  parentBox: {
    gap: 10,
  },
  textStyle: {
    marginBottom: 5,
  },
  skeleton: {marginLeft: 10, borderRadius: 50},
});
